import { useState } from "react";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { OccurrenceAmenitiesError } from "@/api/remoteOccurrenceAmenitiesApi";
import { OccurrenceAmenitiesDialog } from "@/components/tpr/OccurrenceAmenitiesDialog/OccurrenceAmenitiesDialog";
import { OccurrenceAmenityClashList } from "@/components/tpr/OccurrenceAmenitiesDialog/OccurrenceAmenityClashList";
import { useResetOccurrenceAmenities } from "@/hooks/useOccurrenceAmenities";
import type { Booking } from "@/types/Booking";
import type { OccurrenceAmenities, OccurrenceAmenityClash } from "@/types/OccurrenceAmenities";

interface Props {
  booking: Booking;
  selectedDates: string[];
  entriesByDate: Map<string, OccurrenceAmenities>;
  onDone: () => void;
}

/** R27 — sticky actions for the dates ticked in Manage series → Forthcoming. */
export const OccurrenceAmenitySelectionBar = ({ booking, selectedDates, entriesByDate, onDone }: Props) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [resetClashes, setResetClashes] = useState<OccurrenceAmenityClash[]>([]);
  const resetMutation = useResetOccurrenceAmenities(booking.id);

  const sorted = [...selectedDates].sort();
  const customisedSelected = sorted.filter((d) => entriesByDate.get(d)?.customised);
  const sets = sorted.map((d) => [...(entriesByDate.get(d)?.amenityIds ?? [])].sort().join(","));
  const sharedSet = sets.every((s) => s === sets[0]) ? (entriesByDate.get(sorted[0])?.amenityIds ?? []) : null;
  const label = `${sorted.length} date${sorted.length === 1 ? "" : "s"} selected`;

  const handleReset = async () => {
    setResetClashes([]);
    try {
      await resetMutation.mutateAsync({ dates: customisedSelected });
      toast.success(`Reset ${customisedSelected.length} date${customisedSelected.length === 1 ? "" : "s"} to the series default`);
      setConfirmOpen(false);
      onDone();
    } catch (error) {
      if (error instanceof OccurrenceAmenitiesError && error.clashes.length > 0) setResetClashes(error.clashes);
      else toast.error(error instanceof Error ? error.message : "Failed to reset amenities");
    }
  };

  return (
    <div
      className="sticky bottom-0 z-10 mt-2 flex flex-wrap items-center justify-between gap-2 rounded-md border border-indigo-200 bg-indigo-50 px-4 py-2 text-sm"
      data-testid="occurrence-amenity-bar"
    >
      <span className="font-medium text-indigo-900">{label}</span>
      <span className="flex gap-2">
        <Button type="button" size="sm" variant="outline" onClick={onDone}>
          Clear
        </Button>
        <Button type="button" size="sm" variant="outline" disabled={customisedSelected.length === 0} onClick={() => setConfirmOpen(true)}>
          Reset to series default
        </Button>
        <Button type="button" size="sm" onClick={() => setDialogOpen(true)}>
          Change amenities
        </Button>
      </span>

      <OccurrenceAmenitiesDialog
        booking={booking}
        dates={sorted}
        initialAmenityIds={sharedSet}
        isOpen={dialogOpen}
        onOpenChange={setDialogOpen}
        onSaved={onDone}
      />

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reset to series default?</AlertDialogTitle>
            <AlertDialogDescription>
              {customisedSelected.length} date{customisedSelected.length === 1 ? "" : "s"} will go back to the series' usual amenities.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <OccurrenceAmenityClashList clashes={resetClashes} />
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                void handleReset();
              }}
              disabled={resetMutation.isPending}
            >
              Reset
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
